import {
  IconSettingsGear,
  GreyGrey60,
  Inspector,
  Primary12SemiBold,
  SwitchLineField,
  GreyGrey70,
  Box,
  Flex
} from '@sequencehq/core-components'
import { SidebarSection } from '../../QuoteEditorSidebar/widgets/SidebarSection'
import { useMemo } from 'react'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { usePermissions } from 'lib/rbac'
import { PdfCoverImageSection } from './PdfCoverImageSection'

const useQuoteSettings = () => {
  const cubeContext = useCubeContext()

  const groupPricesByPhase = useMemo(
    () =>
      cubeContext.queries.rawData.data.presentation.settings.groupPricesByPhase,
    [cubeContext.queries.rawData.data.presentation.settings.groupPricesByPhase]
  )

  const showTotals = useMemo(
    () => cubeContext.queries.rawData.data.presentation.settings.showTotals,
    [cubeContext.queries.rawData.data.presentation.settings.showTotals]
  )

  const showPdfCoverPage = useMemo(() => {
    const settings = cubeContext.queries.rawData.data.presentation.settings

    if ('showPdfCoverPage' in settings) {
      return settings.showPdfCoverPage
    }

    return false
  }, [cubeContext.queries.rawData.data.presentation.settings])

  const showProductDescriptions = useMemo(() => {
    const settings = cubeContext.queries.rawData.data.presentation.settings

    if ('showProductDescriptions' in settings) {
      return settings.showProductDescriptions
    }

    return false
  }, [cubeContext.queries.rawData.data.presentation.settings])

  const showImagesInPdfBody = useMemo(
    () =>
      cubeContext.queries.rawData.data.presentation.settings
        .showImagesInPdfBody ?? false,
    [cubeContext.queries.rawData.data.presentation.settings.showImagesInPdfBody]
  )

  return {
    groupPricesByPhase: {
      value: groupPricesByPhase,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { groupPricesByPhase: value } }
        })
      }
    },
    showTotals: {
      value: showTotals,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { showTotals: value } }
        })
      }
    },
    showPdfCoverPage: {
      value: showPdfCoverPage,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { showPdfCoverPage: value } }
        })
      }
    },
    showProductDescriptions: {
      value: showProductDescriptions,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { showProductDescriptions: value } }
        })
      }
    },
    showImagesInPdfBody: {
      value: showImagesInPdfBody,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { showImagesInPdfBody: value } }
        })
      }
    },
    showMonthlyRateForAnnualPrices: {
      value:
        cubeContext.queries.rawData.data.presentation.settings
          .showMonthlyRateForAnnualPrices ?? false,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { showMonthlyRateForAnnualPrices: value } }
        })
      }
    },
    columnQuantity: {
      value:
        cubeContext.queries.rawData.data.presentation.settings.columns
          ?.quantity ?? true,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: {
            settings: { columns: { quantity: value } }
          }
        })
      }
    },
    columnLineTotal: {
      value:
        cubeContext.queries.rawData.data.presentation.settings.columns
          ?.lineTotal ?? false,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { columns: { lineTotal: value } } }
        })
      }
    },
    columnBillingPeriods: {
      value:
        cubeContext.queries.rawData.data.presentation.settings.columns
          ?.billingPeriods ?? false,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { columns: { billingPeriods: value } } }
        })
      }
    },
    columnBillingType: {
      value:
        cubeContext.queries.rawData.data.presentation.settings.columns
          ?.billingType ?? true,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { columns: { billingType: value } } }
        })
      }
    },
    hideOpenEndedPhases: {
      value:
        cubeContext.queries.rawData.data.presentation.settings
          ?.hideOpenEndedPhases ?? false,
      onChange: (value: boolean) => {
        cubeContext.mutators.updateData({
          presentation: { settings: { hideOpenEndedPhases: value } }
        })
      }
    }
  }
}

export const COMMON_SWITCH_PROPS = {
  switchSide: 'right' as const
}

const QuoteSettings = () => {
  const ctx = useCubeContext()
  const quoteSettings = useQuoteSettings()
  const { can } = usePermissions()

  const restrictedContentEditing = !can('org.quote:content.update')
  const isDisabled =
    restrictedContentEditing ||
    !ctx.queries.availableFeatures.quote.edit.available.enabled

  return (
    <Inspector.Content>
      <SidebarSection
        title="Settings"
        data-testid="quote.sidebarSection.settings"
      >
        <Box
          {...Primary12SemiBold}
          color={GreyGrey60}
          marginBottom="12px"
          data-testid="quote-inspector-settings-published-quote"
        >
          Published quote
        </Box>
        <Flex direction="column" gap="16px">
          <SwitchLineField
            data-testid="quote-inspector-settings-group-prices-by-phase"
            value={quoteSettings.groupPricesByPhase.value}
            onChange={quoteSettings.groupPricesByPhase.onChange}
            text="Group products by phase"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-show-product-descriptions"
            value={quoteSettings.showProductDescriptions.value}
            onChange={quoteSettings.showProductDescriptions.onChange}
            text="Show product descriptions"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-group-show-totals"
            value={quoteSettings.showTotals.value}
            onChange={quoteSettings.showTotals.onChange}
            text="Show quote total"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-show-monthly-rate-for-annual-prices"
            value={quoteSettings.showMonthlyRateForAnnualPrices.value}
            onChange={quoteSettings.showMonthlyRateForAnnualPrices.onChange}
            text="Show annual prices as monthly"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-hide-open-ended-phases"
            value={quoteSettings.hideOpenEndedPhases.value}
            onChange={quoteSettings.hideOpenEndedPhases.onChange}
            text="Hide open ended phases"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />
        </Flex>
        <Box
          {...Primary12SemiBold}
          color={GreyGrey60}
          marginBottom="12px"
          marginTop="16px"
          data-testid="quote-inspector-settings-columns"
        >
          Columns
        </Box>
        <Flex direction="column" gap="16px">
          <SwitchLineField
            data-testid="quote-inspector-settings-column-billing-type"
            value={quoteSettings.columnBillingType.value}
            onChange={quoteSettings.columnBillingType.onChange}
            text="Billed"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-column-billing-periods"
            value={quoteSettings.columnBillingPeriods.value}
            onChange={quoteSettings.columnBillingPeriods.onChange}
            text="Repeats"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-column-quantity"
            value={quoteSettings.columnQuantity.value}
            onChange={quoteSettings.columnQuantity.onChange}
            text="Quantity"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-column-line-total"
            value={quoteSettings.columnLineTotal.value}
            onChange={quoteSettings.columnLineTotal.onChange}
            text="Total"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />
        </Flex>
        <Box
          {...Primary12SemiBold}
          color={GreyGrey60}
          marginBottom="12px"
          marginTop="16px"
          data-testid="quote-inspector-settings-pdf"
        >
          PDF
        </Box>
        <Flex direction="column" gap="16px">
          <SwitchLineField
            data-testid="quote-inspector-settings-show-inline-images"
            value={quoteSettings.showImagesInPdfBody.value}
            onChange={quoteSettings.showImagesInPdfBody.onChange}
            text="Show inline images"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />

          <SwitchLineField
            data-testid="quote-inspector-settings-show-pdf-cover-page"
            value={quoteSettings.showPdfCoverPage.value}
            onChange={quoteSettings.showPdfCoverPage.onChange}
            text="Show cover page"
            isDisabled={isDisabled}
            {...COMMON_SWITCH_PROPS}
          />
          {quoteSettings.showPdfCoverPage.value && (
            <PdfCoverImageSection isDisabled={isDisabled} />
          )}
        </Flex>
      </SidebarSection>
    </Inspector.Content>
  )
}

export const QuoteSettingsInspectorItem = () => {
  return (
    <Inspector.IconItem
      data-testid="quote-inspector-settings"
      tooltip="Settings"
      content={<QuoteSettings />}
      icon={<IconSettingsGear color={GreyGrey70} />}
    />
  )
}
